import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthenticationContext } from '@/core/context/AuthenticationContext';
import { errorHandler } from '@/core/libs/error-handler';
import { SearchQuery } from '@/modules/matchmaking/models/searchbar/SearchQuery';

import { UseSearchResults } from '../hooks/useSearchResults';
import { useSearchManufacturers } from '../services/queries';
import {
  searchQueryToSearchOptions,
  searchQueryToUrl,
  urlToSearchQuery,
} from '../utils';

export interface SearchContext extends Omit<UseSearchResults, 'fetch'> {
  searchQuery: SearchQuery;
  searchQueryURL: string;
  page: number;
  setSearchQuery(query: SearchQuery): void;
  setSearchQuery(callback: (query: SearchQuery) => SearchQuery): void;
  setPage(page: number): void;
}

const SearchContext = createContext<SearchContext>({
  results: [],
  suggestedResults: [],
  totalManufacturers: 0,
  loading: false,
  pageSize: 10,
  searchQuery: {},
  searchQueryURL: '',
  page: 0,
  setSearchQuery: () => {
    errorHandler.capture(
      'setSearchQuery() being used without initializing SearchQuery context',
      {
        avoidFlashMessage: true,
      },
    );
    throw Error();
  },
  setPage: () => {
    errorHandler.capture(
      'setPage() being used without initializing SearchQuery context',
      { avoidFlashMessage: true },
    );
    throw Error();
  },
});

export interface SearchQueryProviderProps {
  children: JSX.Element;
  autoFetch?: boolean;
}

export function SearchProvider({
  children,
}: SearchQueryProviderProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuthenticationContext();
  const getQryUrlFromPath = (): string =>
    location.pathname.split('/search/')[1];
  const [searchQuery, setSearchQuery] = useState<SearchQuery>(
    urlToSearchQuery(getQryUrlFromPath()),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const getQueryURL = useCallback(
    (): string => searchQueryToUrl(searchQuery),
    [searchQuery],
  );

  const page = useMemo<number>(() => {
    let finalPage = 1;
    const pageParam = searchParams.get('p');
    if (pageParam) {
      try {
        const pageInt = parseInt(pageParam);
        if (!Number.isNaN(pageInt)) {
          finalPage = pageInt;
        }
      } catch {
        finalPage = 1;
      }
    }
    return finalPage;
  }, [searchParams]);

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams({ p: newPage.toString() });
    },
    [setSearchParams],
  );

  // wrapper function that resets page when search query changes
  const setSearchQueryWithPageReset = useCallback(
    (queryOrCallback: SearchQuery | ((query: SearchQuery) => SearchQuery)) => {
      setSearchQuery((oldQuery) => {
        const newQuery =
          typeof queryOrCallback === 'function'
            ? queryOrCallback(oldQuery)
            : queryOrCallback;

        if (JSON.stringify(oldQuery) !== JSON.stringify(newQuery)) {
          // Update URL directly with page 1 to avoid redundant navigations
          const urlFromQry = searchQueryToUrl(newQuery);
          navigate(`/search/${urlFromQry}?p=1`, {
            replace: false,
          });
          // Update the search params to reflect page 1
          setSearchParams({ p: '1' });
        }
        return newQuery;
      });
    },
    [navigate, setSearchParams],
  );
  const {
    data: paginatedManufacturers,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useSearchManufacturers({
    ...searchQueryToSearchOptions(searchQuery),
    page,
    pageSize: 10,
  });

  // Update URL when page changes (search query changes are handled in setSearchQueryWithPageReset)
  useEffect(() => {
    const urlFromQry = getQueryURL();
    navigate(`/search/${urlFromQry}?p=${page}`, {
      replace: false,
    });
  }, [page, navigate, getQueryURL]);

  useEffect(() => {
    refetch();
  }, [currentUser, refetch]);

  useEffect(() => {
    // adjust page when the user tries to access a non-existing/forbidden page manually
    if (page > 1 && paginatedManufacturers?.items?.length === 0) {
      const totalItems = paginatedManufacturers?.meta.totalItems || 0;
      const pageSize = paginatedManufacturers?.meta.itemsPerPage || 10;
      const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));

      setPage(lastPage);
    } else if (page > 10 && !isLoading && isError) {
      setPage(10);
    }
  }, [page, paginatedManufacturers, setPage, isLoading, isError]);

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery: setSearchQueryWithPageReset,
        page,
        setPage,
        searchQueryURL: getQueryURL(),
        suggestedResults: paginatedManufacturers?.suggestedItems || [],
        loading: isFetching,
        totalManufacturers: paginatedManufacturers?.meta.totalItems || 0,
        pageSize: paginatedManufacturers?.meta.itemsPerPage || 10,
        results: paginatedManufacturers?.items || [],
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext(): SearchContext {
  return useContext(SearchContext);
}
